<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <!-- Back link -->
    <div class="oa-page-toolbar">
      <router-link :to="{ name: 'pay-operations-order-amendments' }" class="oa-back-link">
        <i class="fa fa-arrow-left me-1"></i>Back to Queue
      </router-link>
      <h4 class="mb-0"><i class="fa fa-exclamation-circle me-2"></i>Incidents</h4>
    </div>

    <!-- Filters -->
    <div class="card card-absolute rfo-card rfo-card--primary oa-search-card">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0 text-white"><i class="fa fa-filter me-2"></i>Incident Filters</h5>
      </div>
      <div class="card-body border border-primary rounded-3">
        <div class="alert alert-light mb-3" role="alert">
          <i class="fa fa-exclamation-circle font-primary me-2"></i>
          <span v-if="hasAgentAccess">
            Review named incident groups or create one to coordinate related amendment cases.
          </span>
          <span v-else>
            Review named incident groups. An agent or admin role is required to create, rename, or
            close incidents.
          </span>
        </div>
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label small mb-1">Status</label>
            <select
              v-model="filterStatus"
              class="form-select border border-primary form-select-sm"
              @change="fetchIncidents"
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div class="col-12 oa-action-row">
            <button class="btn btn-outline-primary" @click="fetchIncidents">
              <i class="fa fa-refresh me-1"></i>Refresh
            </button>
            <button v-if="hasAgentAccess" class="btn btn-primary" @click="showCreateModal = true">
              <i class="fa fa-plus me-1"></i>New Incident
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading incidents…</span>
      </div>
    </div>

    <!-- Load error -->
    <div
      v-else-if="loadError"
      class="alert alert-danger outline d-flex align-items-center"
      role="alert"
    >
      <span class="flex-grow-1">{{ loadError }}</span>
      <button class="btn btn-sm btn-outline-danger ms-3" @click="fetchIncidents">
        <i class="fa fa-refresh me-1"></i>Retry
      </button>
    </div>

    <!-- Incidents table -->
    <div v-else class="card card-absolute rfo-card rfo-table-card oa-results-card">
      <div class="card-header bg-secondary text-white">
        <h5 class="mb-0 text-white">
          <span><i class="fa fa-list me-2"></i>Incidents</span>
          <span class="oa-card-header-meta ms-2">{{ incidents.length }} shown</span>
        </h5>
      </div>
      <div class="card-body border border-secondary rounded-3 p-0">
        <div class="table-responsive">
          <table class="table table-sm table-striped mb-0">
            <thead class="table-light">
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Orders</th>
                <th>Default Reason</th>
                <th>Created By</th>
                <th>Created</th>
                <!-- Always rendered: the Orders button is available to viewers too. -->
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inc in incidents" :key="inc.id">
                <td>
                  <strong>{{ inc.name }}</strong>
                  <div v-if="inc.description" class="small text-muted">{{ inc.description }}</div>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="inc.status === 'open' ? 'bg-warning text-dark' : 'bg-secondary'"
                  >
                    {{ inc.status }}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 fw-bold text-decoration-none"
                    @click="openIncidentOrders(inc)"
                  >
                    {{ inc.memberOrderUuids?.length || inc.orderCount || 0 }} order(s)
                  </button>
                </td>
                <td class="small">{{ inc.defaultReason || '—' }}</td>
                <td class="small">{{ inc.createdBy || '—' }}</td>
                <td class="small text-nowrap">
                  <ZonedDateTime :value="inc.createdAt" :zone="userTimezone" kind="user" />
                </td>
                <td>
                  <button
                    class="btn btn-sm btn-outline-info me-1"
                    title="View Orders & Execute Batch Correction"
                    @click="openIncidentOrders(inc)"
                  >
                    <i class="fa fa-list me-1"></i>Orders
                  </button>
                  <button
                    v-if="hasAgentAccess"
                    class="btn btn-sm btn-outline-primary me-1"
                    aria-label="Edit incident"
                    @click="editIncident(inc)"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                  <button
                    v-if="hasAgentAccess && inc.status === 'open'"
                    class="btn btn-sm btn-outline-secondary"
                    @click="closeIncident(inc.id)"
                  >
                    Close
                  </button>
                </td>
              </tr>
              <tr v-if="incidents.length === 0">
                <td colspan="7" class="text-center text-muted py-3">No incidents found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="lastPage > 1" class="px-3 pb-3">
        <PaginationFleet
          :current-page="currentPage"
          :total-pages="lastPage"
          :is-loading="isLoading"
          @page-change="goToPage"
        />
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <PayOpsModalShell
      :visible="showCreateModal || !!editingIncident"
      :title="editingIncident ? 'Edit Incident' : 'New Incident'"
      subtitle="Group related problematic orders under a shared operational incident"
      icon="fa-exclamation-circle"
      size="md"
      :close-disabled="isSaving"
      @close="closeModal"
    >
      <div class="mb-3">
        <label class="form-label fw-bold">Name <span class="text-danger">*</span></label>
        <input
          v-model="form.name"
          type="text"
          class="form-control border border-primary"
          placeholder="e.g. TOKHEIM, SB, Viktor"
        />
      </div>
      <div class="mb-3">
        <label class="form-label">Description</label>
        <textarea
          v-model="form.description"
          class="form-control border border-primary"
          rows="2"
        ></textarea>
      </div>
      <div class="mb-3">
        <label class="form-label">Default Reason</label>
        <input
          v-model="form.defaultReason"
          type="text"
          class="form-control border border-primary"
          placeholder="Default amendment reason for this incident"
        />
      </div>
      <template #footer>
        <button class="btn btn-outline-secondary" :disabled="isSaving" @click="closeModal">
          Cancel
        </button>
        <button class="btn btn-primary" :disabled="!form.name || isSaving" @click="saveIncident">
          <span v-if="isSaving"
            ><span class="spinner-border spinner-border-sm me-1"></span>Saving...</span
          >
          <span v-else>{{ editingIncident ? 'Update' : 'Create' }}</span>
        </button>
      </template>
    </PayOpsModalShell>

    <IncidentOrdersModal
      :visible="ordersModalOpen"
      :incident="selectedIncidentForOrders"
      @close="ordersModalOpen = false"
    />

    <PayOpsUsageInstructions
      class="mt-4"
      description="Named groupings of related orders. Replaces the free-text comment column in the legacy spreadsheet."
      :required-roles="incidentRequiredRoles"
      :workflow-notes="incidentWorkflowNotes"
    />
  </div>
</template>

<script lang="ts">
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import PayOpsModalShell from '../shared/PayOpsModalShell.vue';
import IncidentOrdersModal from './components/IncidentOrdersModal.vue';
import PaginationFleet from '@/components/common/PaginationFleet.vue';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';
import { useAmendmentRoles } from '@/services/ryd-pay-operations/order-amendments/use-amendment-roles';
import {
  PAY_OPS_INCIDENT_ROLES,
  buildIncidentsWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';

export default {
  name: 'IncidentsPage',
  components: {
    PayOpsModalShell,
    PayOpsUsageInstructions,
    IncidentOrdersModal,
    PaginationFleet,
    ZonedDateTime,
  },
  setup() {
    const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);
    const toast = useToast();
    const { hasAgentAccess } = useAmendmentRoles();
    const incidents = ref<any[]>([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const filterStatus = ref('');
    const currentPage = ref(1);
    const lastPage = ref(1);
    const showCreateModal = ref(false);
    const editingIncident = ref<any>(null);
    const form = ref({ name: '', description: '', defaultReason: '' });
    const loadError = ref('');

    const ordersModalOpen = ref(false);
    const selectedIncidentForOrders = ref<any>(null);

    function openIncidentOrders(inc: any) {
      selectedIncidentForOrders.value = inc;
      ordersModalOpen.value = true;
    }

    async function fetchIncidents() {
      isLoading.value = true;
      loadError.value = '';
      try {
        const params: Record<string, string> = {
          page: String(currentPage.value),
          pageSize: '20',
        };
        if (filterStatus.value) params.status = filterStatus.value;
        const res = await apiClient.rpIntegration.get(
          API_PATHS.rpIntegration.payOperations.orderAmendments.incidents,
          { params },
        );
        incidents.value = res.data?.items || res.data || [];
        lastPage.value = res.data?.lastPage || 1;
      } catch (err: any) {
        loadError.value = err?.response?.data?.message || 'Failed to load incidents. Try again.';
        toast.error('Failed to load incidents.');
      } finally {
        isLoading.value = false;
      }
    }

    function goToPage(page: number) {
      if (page < 1 || page > lastPage.value) return;
      currentPage.value = page;
      fetchIncidents();
    }

    function editIncident(inc: any) {
      editingIncident.value = inc;
      form.value = {
        name: inc.name || '',
        description: inc.description || '',
        defaultReason: inc.defaultReason || '',
      };
    }

    function closeModal() {
      showCreateModal.value = false;
      editingIncident.value = null;
      form.value = { name: '', description: '', defaultReason: '' };
    }

    async function saveIncident() {
      if (!form.value.name) return;
      isSaving.value = true;
      try {
        if (editingIncident.value) {
          await apiClient.rpIntegration.patch(
            API_PATHS.rpIntegration.payOperations.orderAmendments.incidentById(
              editingIncident.value.id,
            ),
            form.value,
          );
          toast.success('Incident updated.');
        } else {
          await apiClient.rpIntegration.post(
            API_PATHS.rpIntegration.payOperations.orderAmendments.incidents,
            form.value,
          );
          toast.success('Incident created.');
        }
        closeModal();
        await fetchIncidents();
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to save incident.');
      } finally {
        isSaving.value = false;
      }
    }

    async function closeIncident(id: string) {
      try {
        await apiClient.rpIntegration.patch(
          API_PATHS.rpIntegration.payOperations.orderAmendments.incidentById(id),
          { status: 'closed' },
        );
        toast.success('Incident closed.');
        await fetchIncidents();
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to close incident.');
      }
    }

    onMounted(fetchIncidents);

    return {
      incidents,
      isLoading,
      isSaving,
      filterStatus,
      currentPage,
      lastPage,
      showCreateModal,
      editingIncident,
      form,
      loadError,
      ordersModalOpen,
      selectedIncidentForOrders,
      openIncidentOrders,
      fetchIncidents,
      goToPage,
      editIncident,
      closeModal,
      saveIncident,
      closeIncident,
      userTimezone,
      incidentRequiredRoles: PAY_OPS_INCIDENT_ROLES,
      incidentWorkflowNotes: buildIncidentsWorkflowNotes(),
      hasAgentAccess,
    };
  },
};
</script>
